/**
 * Inline SVG marks for sounds that have no spelling.
 *
 * The temptation is to force letters onto them — "brrrr", "pfft", "tsk" — and
 * that is exactly what ASR already does badly. A trill transcribed as "brother"
 * is not a transcription error so much as a category error: the sound has a
 * measured rate and a measured duration, and neither survives being spelled.
 *
 * So each type gets a mark drawn from its own measurements, at the baseline,
 * sized to the line. `cycles` becomes the number of periods in a sine; attack
 * becomes the number of rays in a burst. The letterform count stops being a
 * guess about how emphatic to look.
 *
 * ### Why these stay quiet
 *
 * Ink-coloured, `currentColor`, stroke weights chosen to sit at about the
 * optical weight of the surrounding type. A mark that shouts is a mark that
 * turns a caption line into a caption line with decorations on it, and the
 * point is that the line still scans as one continuous utterance — somebody
 * said "and then", made a noise, and carried on.
 *
 * ### The units
 *
 * Everything is drawn in a 100-unit-tall viewBox with the baseline at y=78 and
 * an x-height of about 50, which is roughly where Roboto Flex puts them. The
 * element is then sized in `em`, so a mark scales with the type around it
 * rather than with the viewport.
 */

const SVG = 'http://www.w3.org/2000/svg';

/** Baseline and x-height inside the 100-unit box. */
const BASE = 78;
const XH = 50;

const svg = (width) => {
  const el = document.createElementNS(SVG, 'svg');
  el.setAttribute('viewBox', `0 0 ${width} 100`);
  el.setAttribute('width', `${(width / 100).toFixed(3)}em`);
  el.setAttribute('height', '1em');
  el.setAttribute('fill', 'none');
  el.setAttribute('stroke', 'currentColor');
  el.setAttribute('stroke-linecap', 'round');
  el.setAttribute('stroke-linejoin', 'round');
  el.setAttribute('aria-hidden', 'true');
  el.classList.add('glyph');
  return el;
};

const path = (parent, d, width) => {
  const p = document.createElementNS(SVG, 'path');
  p.setAttribute('d', d);
  p.setAttribute('stroke-width', String(width));
  parent.append(p);
  return p;
};

const lerp = (a, b, t) => a + (b - a) * Math.max(0, Math.min(1, t));

/**
 * A trill: a sine with one period per measured cycle.
 *
 * The one glyph where the drawing is a measurement rather than an illustration.
 * Ten periods means the lips closed ten times, because `cycles` came from
 * `dur x modRate` — so a long slow "brrrr" is visibly longer and visibly
 * slower than a short fast one, and neither is a designer's guess.
 *
 * Capped at 24 periods: past that the periods are narrower than the stroke and
 * the mark turns into a grey smear, which communicates less than a shorter one.
 */
function trill(event) {
  const cycles = Math.max(1, Math.min(24, event.cycles ?? 3));
  const period = 14;
  const width = cycles * period;
  const amp = lerp(9, 20, event.intensity ?? 0.5);
  const el = svg(width);

  let d = `M 0 ${BASE - XH / 2}`;
  for (let i = 0; i < cycles; i++) {
    const x0 = i * period;
    d +=
      ` C ${x0 + period * 0.25} ${BASE - XH / 2 - amp}` +
      ` ${x0 + period * 0.75} ${BASE - XH / 2 + amp}` +
      ` ${x0 + period} ${BASE - XH / 2}`;
  }
  path(el, d, 5);
  return el;
}

/**
 * A plosive: rays radiating from the burst.
 *
 * Ray count from intensity, which is the attack in dB against a 40 dB ceiling.
 * A hard "pow" throws more rays than a soft "puh" — the mark carries how hard
 * the burst was, which is the one thing a letter cannot.
 */
function plosive(event) {
  const rays = Math.round(lerp(5, 11, event.intensity ?? 0.5));
  const width = 44;
  const el = svg(width);
  const cx = width / 2;
  const cy = BASE - XH / 2;
  const inner = 5;
  const outer = lerp(14, 22, event.intensity ?? 0.5);

  let d = '';
  for (let i = 0; i < rays; i++) {
    const a = (i / rays) * Math.PI * 2 - Math.PI / 2;
    d +=
      ` M ${(cx + Math.cos(a) * inner).toFixed(1)} ${(cy + Math.sin(a) * inner).toFixed(1)}` +
      ` L ${(cx + Math.cos(a) * outer).toFixed(1)} ${(cy + Math.sin(a) * outer).toFixed(1)}`;
  }
  path(el, d.trim(), 4);
  return el;
}

/**
 * A click: one vertical tick.
 *
 * A click is the shortest thing Channel B can detect and the mark is the
 * smallest thing it can draw. Anything more elaborate would claim to know more
 * about a fifteen-millisecond event than fifteen milliseconds contains.
 */
function click() {
  const el = svg(12);
  path(el, `M 6 ${BASE - XH} L 6 ${BASE}`, 4.5);
  return el;
}

/**
 * A fricative: hatching, denser the more zero crossings there were.
 *
 * The spec asks for density from ZCR, and `intensity` for a fricative is
 * carried as its peak ZCR for exactly this — an "sss" hatches tighter than an
 * "shhh" because it genuinely has more energy up high.
 */
function fricative(event) {
  const strokes = Math.round(lerp(4, 9, event.intensity ?? 0.5));
  const gap = 6;
  const width = strokes * gap + 4;
  const el = svg(width);

  let d = '';
  for (let i = 0; i < strokes; i++) {
    const x = 4 + i * gap;
    d += ` M ${x} ${BASE} L ${x + 5} ${BASE - XH}`;
  }
  path(el, d.trim(), 3);
  return el;
}

/**
 * A whistle: a thin line rising across the mark.
 *
 * Thin because a whistle is the least noisy thing in the set — flatness near
 * 0.05, a single narrow peak — and the stroke weight is the one part of a mark
 * that can say "pure" without a caption.
 */
function whistle(event) {
  const width = 34;
  const el = svg(width);
  const rise = lerp(10, 30, event.intensity ?? 0.5);
  path(
    el,
    `M 2 ${BASE - 6} C ${width * 0.4} ${BASE - 6} ${width * 0.5} ${BASE - rise} ${width - 2} ${BASE - rise - 8}`,
    2.5,
  );
  return el;
}

/**
 * A growl: a low, thick scribble with jittered control points.
 *
 * Deterministic jitter, seeded from the event's own duration. A mark that
 * redraws differently every animation frame reads as a glitch rather than as a
 * texture, and this one gets re-rendered whenever the caption line reflows.
 */
function growl(event) {
  const width = 40;
  const el = svg(width);
  const amp = lerp(5, 12, event.intensity ?? 0.5);

  // A small LCG rather than Math.random, so the same event draws the same mark.
  let seed = Math.round((event.dur ?? 200) * 97 + (event.t ?? 0)) >>> 0;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };

  const y = BASE - XH / 3;
  let d = `M 2 ${y}`;
  for (let x = 8; x < width - 2; x += 6) {
    d += ` Q ${x - 3} ${(y + (rnd() - 0.5) * amp * 2).toFixed(1)} ${x} ${(y + (rnd() - 0.5) * amp).toFixed(1)}`;
  }
  path(el, d, 6);
  return el;
}

/**
 * A raspberry.
 *
 * SPEC1.md lists six marks and classifies seven sounds; this is the one it
 * leaves out. Drawn as a wide flat splutter — a doubled wavy line, low and
 * broad — because that is what separates it from the growl above it in the same
 * way flatness separates them in the classifier: same register, more spread.
 */
function raspberry(event) {
  const width = 46;
  const el = svg(width);
  const amp = lerp(5, 10, event.intensity ?? 0.5);
  const y = BASE - XH / 3;

  for (const [offset, weight] of [
    [0, 5],
    [9, 3],
  ]) {
    let d = `M 2 ${y + offset}`;
    for (let x = 10; x < width - 2; x += 9) {
      d += ` Q ${x - 4} ${(y + offset - amp).toFixed(1)} ${x} ${y + offset}`;
      d += ` Q ${x + 4} ${(y + offset + amp).toFixed(1)} ${x + 9} ${y + offset}`;
    }
    path(el, d, weight);
  }
  return el;
}

const GLYPHS = { trill, plosive, click, fricative, whistle, growl, raspberry };

/**
 * An inline mark for one gesture, or null for a type with no drawing.
 *
 * @param {import('../core/types.js').GestureEvent} event
 * @returns {?SVGElement}
 */
export function glyphFor(event) {
  const make = GLYPHS[event.type];
  if (!make) return null;

  const el = make(event);
  // Confidence is worn as opacity. A gesture the vision gate could not confirm
  // is still shown — Channel B has to work with the camera off — but it is
  // shown as the weaker claim it is.
  const confidence = event.confidence ?? 1;
  if (confidence < 1) el.style.opacity = String(Math.max(0.45, confidence));

  el.dataset.type = event.type;
  const detail = event.modRate ? `${event.modRate.toFixed(0)} Hz, ${event.cycles} cycles` : null;
  el.setAttribute('role', 'img');
  el.setAttribute(
    'aria-label',
    `${event.type}${detail ? `, ${detail}` : ''}${event.place && event.place !== 'unknown' ? `, ${event.place}` : ''}`,
  );
  return el;
}

/** The types this module can draw, for the legend. */
export const GLYPH_TYPES = Object.keys(GLYPHS);

export default glyphFor;
